import * as React from 'react'
import Link from 'next/link'

import { Theme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import Stack from '@mui/material/Stack'
import Grid from '@mui/material/Grid'

import { usePathsProgress, useCoursesProgress } from 'components/hooks/state'
import { I18n, useTranslation } from 'components/hooks/use-translation'
import ProgressCard from 'components/atomic/molecules/progress-card'
import Button from 'components/atomic/atoms/button'
import Text from 'components/atomic/atoms/text'

import { PathGoal } from 'types/types'
import Routes from 'utils/routes'

interface ProgressesProps {
    showAll?: boolean
}

export default React.memo((props: ProgressesProps) => {
    const smallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'))
    const { t }: I18n = useTranslation()

    const { data: pathsProgress = [] } = usePathsProgress()
    const { data: coursesProgress = [] } = useCoursesProgress()

    const progresses = React.useMemo(() => {
        return [
            ...pathsProgress.map((p) => ({ ...p, type: p.goal, to: Routes.Path(p.slug) })),
            ...coursesProgress.map((c) => ({ ...c, type: 'course', to: Routes.Course(c.slug) })),
        ].sort((a, b) => (a.updated_at > b.updated_at ? -1 : 1))
    }, [pathsProgress, coursesProgress])

    if (!progresses.length) return null

    const visibleItems = props.showAll ? progresses.length : smallScreen ? 1 : 3

    return (
        <Stack gap={2}>
            <Stack gap={2} justifyContent="space-between" alignItems="center" direction="row">
                <Text variant="h5">{t('catalog:collections.progresses')}</Text>
                {visibleItems < progresses.length && (
                    <Link key={Routes.CatalogPersonal} href={Routes.CatalogPersonal} passHref={true}>
                        <Button sx={{ flex: 'none' }}>{t('catalog:buttons.showAll')}</Button>
                    </Link>
                )}
            </Stack>
            <Grid spacing={2} container>
                {progresses.slice(0, visibleItems).map(({ type, ...progress }) => (
                    <Grid item xs lg={4} key={progress._id}>
                        <ProgressCard type={type as PathGoal} {...progress} />
                    </Grid>
                ))}
            </Grid>
        </Stack>
    )
})
